import { z } from 'zod'
import { UTCMillisecondsSchema } from '../time'
import { SmallUserSchema, type SmallUser } from '../smallUser'
import { TinyRoleSchema, type RoleId } from '../role'
import { PermissionMapSchema } from '../permissions/types'
import { LanguageCodeSchema } from '../languageCode'
import { WithImportIdSchema } from '../import'
import { AccountIdSchema, CompanyIdSchema, TeamIdSchema, UserIdSchema, isUserId, type UserId } from '../ids'
import { TinyTeamSchema, TinyUserSchema, UserTeamsByLevelSchema, type TinyUser } from '../entity'
import { CustomFieldMapSchema } from '../customFieldMap'
import { stubCompanyId } from '../company/model'
import { AuditPropsSchema, UPLOAD_URL_DESCRIPTION } from '../common'
import { CloserSettingsSchema } from '../closer'
import { ArchivableSchema } from '../archive'
import { AddressSchema } from '../address'

export const BirthDateSchema = z.object({
  month: z.coerce.number().optional(),
  day: z.coerce.number().optional(),
})
export type BirthDate = z.infer<typeof BirthDateSchema>

export const PrimaryTeamSchema = z
  .object({
    teamId: TeamIdSchema.meta({ description: 'Team ID for the primary team', example: 'Team:abc123' }),
    staleAfter: z.number().meta({ description: 'When the team assignment becomes stale, in UTC epoch milliseconds' }),
  })
  .meta({
    id: 'primaryTeam',
    title: 'Primary Team',
    description: 'Primary team assignment for a user',
  })
export type PrimaryTeam = z.infer<typeof PrimaryTeamSchema>

export const SelfReportedManagerSchema = z
  .object({
    managerId: UserIdSchema.optional().meta({
      id: 'selfReportedManagerId',
      description: "User ID of the user's self-reported manager",
      example: 'U:manager123',
    }),
    updatedAt: UTCMillisecondsSchema.meta({
      id: 'selfReportedManagerUpdatedAt',
      description: 'When the self-reported manager was last updated, in UTC epoch milliseconds',
    }),
  })
  .meta({
    id: 'selfReportedManager',
    title: 'Self Reported Manager',
    description: 'Manager selection reported directly by the user',
  })
export type SelfReportedManager = z.infer<typeof SelfReportedManagerSchema>

export const UnsavedUserSchema = z
  .object({
    ...ArchivableSchema.shape,
    ...WithImportIdSchema.shape,
    ...SmallUserSchema.pick({
      phone: true,
      firstName: true,
      lastName: true,
      preferredName: true,
      email: true,
      timeZone: true,
      clientUserId: true,
      title: true,
      closerStatus: true,
    }).shape,
    companyId: CompanyIdSchema.optional().meta({
      description: 'Company ID the user belongs to',
      example: 'C:company123',
    }),
    managerId: UserIdSchema.optional().meta({
      id: 'userManagerId',
      description: "User ID of the user's manager",
      example: 'U:manager123',
    }),
    hasManager: z.boolean().default(true).optional().meta({
      id: 'userHasManager',
      description: 'Whether the user reports having a manager',
      example: true,
    }),
    selfReportedManager: SelfReportedManagerSchema.optional().meta({
      description: "User's self-reported manager selection",
    }),
    primaryTeam: PrimaryTeamSchema.optional().meta({ description: "User's primary team assignment" }),
    betaProgram: z.boolean().optional().meta({
      id: 'userBetaProgram',
      description: 'Whether the user is enrolled in the Terros Beta program',
      example: true,
    }),
    language: z
      .string()
      .optional()
      .meta({ id: 'userLanguage', description: "User's preferred language", example: 'en_US' }),
    secondaryLanguages: z
      .array(LanguageCodeSchema)
      .optional()
      .meta({ id: 'userSecondaryLanguages', description: 'Languages spoken by user', example: 'en' }),
    color: z
      .string()
      .optional()
      .meta({ id: 'userColor', description: 'UI color theme for the user', example: '#3B82F6' }),
    superuserScore: z.number().optional().meta({
      id: 'userSuperuserScore',
      description: 'Score indicating superuser status, used for internal ranking',
      example: 42.12,
    }),
    customFields: CustomFieldMapSchema.optional().meta({
      description: "User's custom fields for additional data",
      example: { 'CF.1': 'value1', 'CF.2': 'value2' },
    }),
    accountId: AccountIdSchema.optional().meta({
      description: 'The accountId the user is associated with, only applicable for recruiting feature',
    }),
    whitelist: z.boolean().optional().meta({
      id: 'userWhitelist',
      description: 'Whether the user is whitelisted, not allowing them to be archived',
      example: false,
    }),
    closer: CloserSettingsSchema.optional().meta({ description: "User's closer settings and preferences" }),
  })
  .meta({
    id: 'unsavedUser',
    title: 'Unsaved User',
    description: 'User data before being saved to the system',
  })
export type UnsavedUser = z.infer<typeof UnsavedUserSchema>

const UserTeamDataBaseSchema = z.object({
  manages: z.array(TeamIdSchema).default([]),
  directMemberOf: z.array(TinyTeamSchema).default([]),
  memberOf: z.array(TinyTeamSchema).default([]).meta({ description: 'Includes all upline teams' }),
  managers: z.array(TinyUserSchema).default([]).meta({ description: 'All possible managers for this user' }),
  manager: TinyUserSchema.optional().meta({ description: "User's primary manager details" }),
  groups: z.array(TinyTeamSchema).default([]).meta({ description: 'All groups the user belongs to', deprecated: true }),
})
export const UserTeamDataSchema = UserTeamDataBaseSchema.and(UserTeamsByLevelSchema)
export type UserTeamData = z.infer<typeof UserTeamDataSchema>

export const NotificationPreferenceSchema = z.object({
  push: z.boolean().optional(),
  email: z.boolean().optional(),
  // text: z.boolean().optional(),
})

export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>

export const UserPreferencesSchema = z.object({
  /**
   * false disables that channel for every notification type.
   * true or unset falls through to the per-type preference.
   */
  all: NotificationPreferenceSchema.optional(),
  /** notify user when demo setup fails, only for terros users */
  demoSetupFailed: NotificationPreferenceSchema.optional(),
  /** notify user when demo setup is complete, only for terros users */
  demoSetupComplete: NotificationPreferenceSchema.optional(),
  /** notify dispatcher whenever a close appointment is created */
  dispatcherAssignment: NotificationPreferenceSchema.optional(),
  /** notify closer whenever a close appointment is created/updated */
  closeEvents: NotificationPreferenceSchema.optional(),
  /** notify manager when a shadow event is created */
  shadowEvents: NotificationPreferenceSchema.optional(),
  /** notify when it's time to submit a plan */
  submitPlan: NotificationPreferenceSchema.optional(),
  /** notify manager when a plan is submitted */
  planSubmitted: NotificationPreferenceSchema.optional(),
  /** notify employee after there plan is reviewed */
  planReviewed: NotificationPreferenceSchema.optional(),
  interviews: NotificationPreferenceSchema.optional(),
  /** meeting check-in notifications, including manager nudges */
  meetings: NotificationPreferenceSchema.optional(),
  /** notify manager an evaluator need to be assigned */
  evaluationAssignment: NotificationPreferenceSchema.optional(),
  /** notify manager after an evaluation is requested*/
  evaluationRequest: NotificationPreferenceSchema.optional(),
  /** notify evaluator that evaluation is ready to be reviewed */
  evaluationReview: NotificationPreferenceSchema.optional(),
  /** notify employee that evaluation has been updated */
  evaluationUpdate: NotificationPreferenceSchema.optional(),
  tasks: NotificationPreferenceSchema.optional(),
  messages: NotificationPreferenceSchema.optional(),
  /** notify employee when an area is assigned */
  areaAssignment: NotificationPreferenceSchema.optional(),
  /** notify the team's managers when an area is assigned to or removed from their team */
  teamAreaAssignment: NotificationPreferenceSchema.optional(),
  /** notify user when invited to a game */
  game: NotificationPreferenceSchema.optional(),
  recordingScored: NotificationPreferenceSchema.optional(),
  recordingAssigned: NotificationPreferenceSchema.optional(),
  recordingFeedbackAdded: NotificationPreferenceSchema.optional(),
  recordingEventSuggestion: NotificationPreferenceSchema.optional(),

  cardViewed: NotificationPreferenceSchema.optional(),
  cardContactSaved: NotificationPreferenceSchema.optional(),
})
export type UserPreferences = z.infer<typeof UserPreferencesSchema>

export type UserPreferenceKeys = Exclude<keyof NonNullable<UserPreferences | undefined>, 'all'>

/** An unsaved push channel is on. An unsaved email channel is off. */
export function defaultNotificationChannelEnabled(channel: keyof NotificationPreference): boolean {
  return channel === 'push'
}

/** `all` false disables the channel. Otherwise the per-type value, or the unsaved channel default. */
export function canSendNotification(
  userPref: UserPreferences | undefined,
  kind: UserPreferenceKeys,
  type: keyof NotificationPreference
): boolean {
  if (userPref?.all?.[type] === false) return false
  return userPref?.[kind]?.[type] ?? defaultNotificationChannelEnabled(type)
}

export const UserDataSchema = z
  .object({
    ...UnsavedUserSchema.shape,
    ...TinyUserSchema.pick({ lastAccess: true, avatarBlurhash: true, avatarUrl: true }).shape,
    userId: UserIdSchema.meta({ description: 'Unique user identifier', example: 'U:user123' }),
    companyId: CompanyIdSchema.meta({ description: 'Company ID the user belongs to', example: 'C:company123' }),
    firstAccess: z
      .number()
      .optional()
      .meta({ id: 'userFirstAccess', description: "Timestamp of user's first login", example: 1672531200000 }),
    startDate: z
      .number()
      .optional()
      .meta({
        id: 'userStartDate',
        description: "User's employment start date, in UTC epoch milliseconds",
        example: 1672531200000,
      }),
    clientTeamId: z
      .string()
      .optional()
      .meta({ id: 'userClientTeamId', description: 'External client system team identifier', example: 'TEAM001' }),
    teams: UserTeamDataSchema.optional().meta({
      description:
        "User's team memberships data. Dynamic team level keys are included as team{level}, such as team1, team2, or team12.",
      example: {
        manages: ['Team:manager'],
        directMemberOf: [{ teamId: 'Team:direct', name: 'Direct Team', level: 1 }],
        memberOf: [{ teamId: 'Team:member', name: 'Member Team', level: 1 }],
        managers: [{ userId: 'U:manager', firstName: 'Jane', lastName: 'Manager' }],
        team1: [{ teamId: 'Team:member', name: 'Member Team', level: 1 }],
        team12: [{ teamId: 'Team:regional', name: 'Regional Team', level: 12 }],
      },
    }),
    roles: z.array(TinyRoleSchema).optional().meta({ description: "User's assigned roles" }),
    biography: z.string().optional().meta({
      id: 'userBiography',
      description: "User's biography or personal description",
      example: 'Experienced sales professional...',
    }),
    notificationPrefs: UserPreferencesSchema.optional().meta({ description: "User's notification preferences" }),
    agreeToTerms: z
      .number()
      .optional()
      .meta({ description: 'Timestamp when user agreed to terms', example: 1672531200000 }),
    agreeToLicensing: z
      .number()
      .optional()
      .meta({ description: 'Timestamp when user agreed to licensing', example: 1672531200000 }),
    agreeToAITraining: z
      .boolean()
      .optional()
      .meta({ description: 'Whether the user agreed to AI training on their pitch recordings' }),
  })
  .meta({
    id: 'userData',
    title: 'User Data',
    description: 'Complete user information including system-generated fields',
  })
export type UserData = z.infer<typeof UserDataSchema>

export const UserProfileDataSchema = z
  .object({
    ...UserDataSchema.shape,
    ...AuditPropsSchema.shape,
    permissionMap: PermissionMapSchema.optional(),
    mailingAddress: AddressSchema.optional().meta({ description: "User's mailing address" }),
    shoeSize: z.string().optional().meta({ description: "User's shoe size for company events", example: '10.5' }),
    shirtSize: z.string().optional().meta({ description: "User's shirt size for company events", example: 'L' }),
    birth: BirthDateSchema.optional().meta({ description: "User's birth date information" }),
    uploadAvatarUrl: z.string().optional().meta({ description: UPLOAD_URL_DESCRIPTION }),
    closerIds: UserIdSchema.array().optional().meta({ description: 'List of closer user IDs assigned to this user' }),
    directRoles: TinyRoleSchema.array()
      .optional()
      .meta({ description: "User's directly assigned roles, not including roles they have on teams" }),
  })
  .meta({
    id: 'userProfileData',
    title: 'User Profile Data',
    description: 'Extended user profile information including private details',
  })
export type UserProfileData = z.infer<typeof UserProfileDataSchema>

// Utility types and functions from the old user.ts file
export const UserIdentifierSchema = z.string().describe('User ID, email, accountId, or clientUserId')
export type UserIdentifier = z.infer<typeof UserIdentifierSchema>

/** used in unit tests */
export function stubUserId(id?: string): UserId {
  return `U:${id ?? 'ID'}`
}

export const WithUserIdSchema = z.object({
  userId: UserIdSchema,
})

export type WithUserId = z.infer<typeof WithUserIdSchema>

export const SYSTEM_USER_ID: UserId = 'U:system'
export function isSystemUser(userId: UserId): boolean {
  return userId.startsWith(SYSTEM_USER_ID)
}

export const SYSTEM_UNASSIGNED_USER_ID: UserId = `${SYSTEM_USER_ID}_unassigned`
export function isUnassignedUser(userId: UserId): boolean {
  return userId.startsWith(SYSTEM_UNASSIGNED_USER_ID)
}

export const SYSTEM_ARCHIVED_USER_ID: UserId = `${SYSTEM_USER_ID}_archived`
export function isSystemArchivedUser(userId: UserId): boolean {
  return userId.startsWith(SYSTEM_ARCHIVED_USER_ID)
}

export const SYSTEM_PLACEHOLDER_USER_ID: UserId = `${SYSTEM_USER_ID}_placeholder`

export const UNASSIGNED_USER_ID: UserId = 'U.unassigned'
export function isUnassignedUserId(userId: UserId): boolean {
  return userId === UNASSIGNED_USER_ID
}

export const SYSTEM_CONNECT_USER_ID: UserId = `${SYSTEM_USER_ID}_connect`

export const toUserId = (user: WithUserId | UserId): UserId => (isUserId(user) ? user : user.userId)

/** deterministically returns a number between 0 and 9 */
export function getTestGroup(userId: UserId): number {
  const id = userId.substring(2, 10).replace(/-/g, '')
  const result = parseInt(id, 16)
  if (isNaN(result)) {
    const result2 = parseInt(id, 32)
    if (isNaN(result2)) return 0
    return result2 % 10
  }
  return result % 10
}

/**
 * Build a TinyUser object from a more full UserData object
 */
export function toTinyUser(profile: TinyUser): TinyUser {
  return {
    userId: profile.userId,
    lastName: profile.lastName,
    firstName: profile.firstName,
    preferredName: profile.preferredName,
    avatarBlurhash: profile.avatarBlurhash,
    avatarUrl: profile.avatarUrl,
    lastAccess: profile.lastAccess,
    clientUserId: profile.clientUserId,
    isDeleted: profile.isDeleted,
    color: profile.color,
  }
}

export function toSmallUser(profile: SmallUser): SmallUser {
  return {
    ...toTinyUser(profile),
    email: profile.email,
    phone: profile.phone,
    title: profile.title,
    timeZone: profile.timeZone,
    closerStatus: profile.closerStatus,
    roles: profile.roles,
    memberOf: profile.memberOf,
    companyId: profile.companyId,
    createdDate: profile.createdDate,
  }
}

/** used in unit tests */
export function stubUnsavedUser(data?: Partial<UnsavedUser>): UnsavedUser {
  return {
    lastName: 'Doe',
    firstName: 'John',
    email: 'john@doe.com',
    ...data,
  }
}

/** used in unit tests */
export function stubUserData(data?: Partial<UserData>): UserData {
  return {
    ...stubUnsavedUser(),
    companyId: stubCompanyId(),
    userId: stubUserId(),
    ...data,
  }
}

/** used in unit tests */
export function stubUserProfileData(data?: Partial<UserProfileData>): UserProfileData {
  return {
    ...stubUserData(),
    ...data,
  }
}

export const UserDefault = {
  language: 'en_US',
  timeZone: 'America/Los_Angeles',
}

export const unknownLinksBiographyPattern = /(?<temp1>https?:\/\/[^\s]+)/g

export const knownLinksBiographyPattern: RegExp[] = [
  /^https?:\/\/(?:www\.)?youtube\.com\/(?:(?:@[a-zA-Z0-9-_]{1,100})|channel\/|user\/|c\/)?[a-zA-Z0-9_-]+\/?$/,
  /^https?:\/\/(?:www\.)?x\.com\/[a-zA-Z0-9_]{1,15}\/?$/,
  /^https?:\/\/(?:www\.)?twitter\.com\/[a-zA-Z0-9_]{1,15}\/?$/,
  /^https?:\/\/(?:www\.)?instagram\.com\/[a-zA-Z0-9_.]{1,30}\/?$/,
  /^https?:\/\/(?:www\.)?linktr\.ee\/[a-zA-Z0-9-_]{1,30}\/?$/,
]

/** @deprecated - use roles instead */
export type TeamMembershipType = 'manager' | 'member'

export type UserImport = {
  raw: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  manager?: string
  teamName?: string
  externalId?: string
  timezone?: string
  preferredName?: string
  closerStatus?: string
  teamRoles?: RoleId[]
  directRoles?: RoleId[]
  isDeleted?: boolean
}

/** @see https://help.okta.com/en-us/content/topics/users-groups-profiles/usgp-end-user-states.htm */
export type SunrunUserStatus =
  | 'STAGED'
  | 'PROVISIONED'
  | 'ACTIVE'
  | 'RECOVERY'
  | 'PASSWORD EXPIRED'
  | 'LOCKED OUT'
  | 'SUSPENDED'
  | 'DEPROVISIONED'

export type SunrunOktaUser = {
  oktaUserId: string
  status: SunrunUserStatus
  firstName: string
  lastName: string
  nickName: string
  office: string
  title: string
  email: string
  phone: string
  employeeId?: string
}

/** @deprecated replaced by dashboards */
export type UserStats = {
  pitchesToday: number
  pitchesThisWeek: number
  commissionThisQuarter: number
  /** not empty if for another user */
  userId?: UserId
}

export const UserLoginTypeSchema = z.enum(['success', 'failed'])
export type UserLoginType = z.infer<typeof UserLoginTypeSchema>

export const UserLoginHistoryItemSchema = z.object({
  timestamp: UTCMillisecondsSchema.meta({
    description: 'Time of the recorded identity-provider authentication event, in Unix milliseconds',
    example: 1760000000000,
  }),
  type: UserLoginTypeSchema.meta({
    description: 'Whether identity-provider authentication succeeded or failed',
    example: 'success',
  }),
  description: z.string().optional().meta({
    description: 'Identity-provider description of the authentication event',
    example: 'Success Login',
  }),
})
export type UserLoginHistoryItem = z.infer<typeof UserLoginHistoryItemSchema>

export const UserMetadataSchema = z.object({
  developer: z.boolean().default(false),
})
export type UserMetadata = z.infer<typeof UserMetadataSchema>
